import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminServices.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaCloud, FaCode, FaMobileAlt, FaDraftingCompass, FaSave, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const AdminServices = () => {
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [formData, setFormData] = useState({ title: '', description: '', icon: 'code' });

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        setIsLoading(true);
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/services', {
                headers: {
                    'Accept': 'application/json'
                }
            });
            if (ok) {
                setServices(Array.isArray(result) ? result : result.data || []);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            toast.error('Failed to load services');
        } finally {
            setIsLoading(false);
        }
    };

    const openModal = (service = null) => {
        if (service) {
            setEditingService(service);
            setFormData({ title: service.title, description: service.description, icon: service.icon });
        } else {
            setEditingService(null);
            setFormData({ title: '', description: '', icon: 'code' });
        }
        setIsModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const method = editingService ? 'PUT' : 'POST';
        const url = editingService
            ? `https://aayushrealproperties.in/nimaibackend/public/api/services/${editingService.id}`
            : 'https://aayushrealproperties.in/nimaibackend/public/api/services';

        try {
            const { ok, data: errorData } = await safeFetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (ok) {
                toast.success(editingService ? 'Service updated successfully' : 'Service created successfully');
                fetchServices();
                setIsModalOpen(false);
            } else {
                toast.error(errorData.message || 'Failed to save service');
            }
        } catch (error) {
            console.error('Error saving service:', error);
            toast.error('Network error while saving');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this service?')) return;

        try {
            const { ok } = await safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/services/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });

            if (ok) {
                setServices(services.filter(s => s.id !== id));
                toast.success('Service deleted successfully');
            } else {
                toast.error('Failed to delete service');
            }
        } catch (error) {
            console.error('Error deleting service:', error);
            toast.error('Network error while deleting');
        }
    };

    const getIcon = (iconName) => {
        switch (iconName?.toLowerCase()) {
            case 'cloud': return <FaCloud />;
            case 'code': return <FaCode />;
            case 'mobile': return <FaMobileAlt />;
            case 'design': return <FaDraftingCompass />;
            default: return <FaCode />;
        }
    };

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Service <span>Management</span></h1>
                        <p>Manage the core offerings displayed on your homepage.</p>
                    </div>
                    <button className="add-service-btn" onClick={() => openModal()}>
                        <FaPlus /> Add New Service
                    </button>
                </header>

                <div className="services-admin-grid">
                    {isLoading ? (
                        <div className="admin-loading-container" style={{ textAlign: 'center', gridColumn: '1/-1', padding: '50px' }}>
                            <div className="admin-loader"></div>
                            <p style={{ marginTop: '20px', color: '#64748b' }}>Loading services...</p>
                        </div>
                    ) : services.length > 0 ? (
                        services.map((service, index) => (
                            <motion.div
                                key={service.id}
                                className="admin-service-card"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <div className="card-top">
                                    <div className="service-icon-box">
                                        {getIcon(service.icon)}
                                    </div>
                                    <div className="card-actions">
                                        <button className="action-btn edit" onClick={() => openModal(service)} title="Edit">
                                            <FaEdit />
                                        </button>
                                        <button className="action-btn delete" onClick={() => handleDelete(service.id)} title="Delete">
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                                <h3>{service.title}</h3>
                                <p>{service.description}</p>
                            </motion.div>
                        ))
                    ) : (
                        <div className="empty-state" style={{ gridColumn: '1/-1', textAlign: 'center', padding: '50px' }}>
                            <h3>No services found</h3>
                            <p>Click "Add New Service" to get started.</p>
                        </div>
                    )}
                </div>

                {/* Add/Edit Modal */}
                <AnimatePresence>
                    {isModalOpen && (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="admin-modal"
                                initial={{ y: 50, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 50, opacity: 0 }}
                            >
                                <div className="modal-header">
                                    <h2>{editingService ? 'Edit Service' : 'Add New Service'}</h2>
                                    <button className="close-modal" onClick={() => setIsModalOpen(false)}>
                                        <FaTimes />
                                    </button>
                                </div>
                                <form onSubmit={handleSave} className="admin-form">
                                    <div className="form-group">
                                        <label>Service Title</label>
                                        <input
                                            type="text"
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            placeholder="e.g. Web Development"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Select Icon</label>
                                        <select
                                            value={formData.icon}
                                            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                        >
                                            <option value="cloud">Cloud</option>
                                            <option value="code">Code</option>
                                            <option value="mobile">Mobile</option>
                                            <option value="design">Design</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Description</label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Briefly describe the service..."
                                            rows="4"
                                            required
                                        ></textarea>
                                    </div>
                                    <div className="form-footer">
                                        <button type="button" className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                                        <button type="submit" className="btn-primary">
                                            <FaSave /> {editingService ? 'Update Service' : 'Create Service'}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default AdminServices;
